"use client";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, Shield, Sparkles, GraduationCap } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const Pill = ({ children, tone = "gray", size = "md" }) => {
  const toneMap = {
    red: "bg-red-50 text-red-700 ring-red-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    gray: "bg-gray-50 text-gray-700 ring-gray-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
    yellow: "bg-yellow-50 text-yellow-700 ring-yellow-100",
  };
  const sizeMap = {
    sm: "px-2.5 py-1 text-[11px]",
    md: "px-3 py-1.5 text-xs",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full font-extrabold ring-1 whitespace-nowrap ${
        toneMap[tone] || toneMap.gray
      } ${sizeMap[size] || sizeMap.md}`}
    >
      {children}
    </span>
  );
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, error } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        window.location.href = "/dashboard";
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb] flex items-center justify-center px-4">
      <div className="w-full max-w-6xl">
        

        {/* LOGIN FORM */}
        <div className="flex justify-center">
          <div className="w-full max-w-md">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
              <div className="p-8">
                {/* Form Header */}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-red-50 ring-1 ring-red-100 mb-4">
                    <Shield className="h-8 w-8 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-extrabold text-gray-900 mb-2">
                    Administrator Login
                  </h2>
                  <p className="text-sm text-gray-600">
                    R-Ed Labs Administration Panel
                  </p>
                </div>

                {/* Login Form */}
                <form onSubmit={handleLogin} className="space-y-6">
                  {/* Email Field */}
                  <div>
                    <label
                      htmlFor="admin-email"
                      className="block text-sm font-extrabold text-gray-900 mb-2"
                    >
                      Admin Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        id="admin-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="admin@redlabs.com"
                        className="w-full pl-12 pr-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none transition-all duration-200 bg-white"
                        aria-describedby={error ? "login-error" : undefined}
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div>
                    <label
                      htmlFor="admin-password"
                      className="block text-sm font-extrabold text-gray-900 mb-2"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        id="admin-password"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter admin password"
                        className="w-full pl-12 pr-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none transition-all duration-200 bg-white"
                        aria-describedby={error ? "login-error" : undefined}
                      />
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div
                      id="login-error"
                      className="rounded-2xl bg-red-50 ring-1 ring-red-200 p-4"
                      role="alert"
                      aria-live="polite"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <Shield className="h-4 w-4 text-red-600" />
                        </div>
                        <div>
                          <div className="text-sm font-extrabold text-red-900">Authentication Failed</div>
                          <p className="text-sm text-red-700 mt-1">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-red-600 text-white py-3 rounded-2xl font-extrabold text-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-describedby="login-button-description"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        <span>Signing In...</span>
                      </div>
                    ) : (
                      "Login as Admin"
                    )}
                  </button>
                  <div id="login-button-description" className="sr-only">
                    Click to sign in to the administrator panel
                  </div>
                </form>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-center gap-2">
                    <Pill tone="gray" size="sm">
                      <Lock className="h-3 w-3 mr-1" />
                      Secure Access
                    </Pill>
                    <Pill tone="amber" size="sm">
                      Authorized Personnel Only
                    </Pill>
                  </div>
                  <p className="text-xs text-center text-gray-500 mt-3">
                    This portal is restricted to authorized administrators only.
                    All access attempts are logged and monitored.
                  </p>
                </div>
              </div>
            </div>

            
          </div>
        </div>
      </div>
    </div>
  );
}
